import React, { useState, useEffect } from "react";
import "./users.css";
import { useLocation } from "react-router-dom";
import {
  listUsers,
  registerUser,
  deleteUser,
  updateUser,
} from "../../../services/api/users";
import {
  deleteRealEstate,
  listRealEstates,
  updateRealEstate,
  createRealEstate,
} from "../../../services/api/real_estates";
import UserManipulation from "./components/UserManipulation";
import ModalConfirmAction from "../../components/ModalConfirmAction";
import { FaUserEdit, FaTrashAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { decodeJWT } from "../../../utils/getUserByToken";
import { logout } from "../../../services/api/logout";
import Spinner from "../../components/Spinner";

export default function Users() {
  const location = useLocation();
  const pageType =
    location.pathname === "/dashboard/entidades" ? "real_estate" : "user";

  const [entities, setEntities] = useState([]);
  const [search, setSearch] = useState("");
  const [entityToEdit, setEntityToEdit] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState({
    id: null,
    type: null,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [realEstateOptions, setRealEstateOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  const totalPages = Math.ceil(totalItems / limit) || 1;

  const fetchAndSetEntities = async () => {
    try {
      if (pageType === "user") {
        const response = await listUsers(currentPage, limit);
        
        let usersData = [];
        if (response && response.data) {
          usersData = response.data;
          setTotalItems(response.total || 0);
        } else {
          usersData = response || [];
          setTotalItems(usersData.length);
        }

        const realEstatesData = await listRealEstates();

        const processedUsers = (
          usersData && Array.isArray(usersData) ? usersData : []
        ).map((u) => ({
          ...u,
          entityType: "user",
          displayName: u.full_name,
          displayDocument: u.document,
        }));

        const processedRealEstates = (
          realEstatesData && Array.isArray(realEstatesData)
            ? realEstatesData
            : []
        )
          .filter((re) => re.is_payer === true)
          .map((re) => ({
            ...re,
            entityType: "real_estate",
            displayName: re.full_name || re.name,
            displayDocument: re.document || re.cnpj,
            email: re.email || re.legal_representative_email || "",
          }));

        setRealEstateOptions(processedRealEstates);
        setEntities(processedUsers);
      } else {
        const realEstatesData = await listRealEstates();
        const processedRealEstates = (
          realEstatesData && Array.isArray(realEstatesData)
            ? realEstatesData
            : []
        )
          .filter((re) => re.type !== "payer")
          .map((re) => ({
            ...re,
            entityType: "real_estate",
            displayName: re.full_name || re.name,
            displayDocument: re.document || re.cnpj,
            email: re.email || re.legal_representative_email || "",
          }));
        setEntities(processedRealEstates);
        setRealEstateOptions([]);
        setTotalItems(processedRealEstates.length);
      }
    } catch (error) {
      console.error("Erro geral em fetchAndSetEntities:", error);
      setEntities([]);
      setRealEstateOptions([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = decodeJWT(token);
        if (decoded && decoded.position) {
          await fetchAndSetEntities();
        } else {
          logout();
        }
      } else {
        logout();
      }
      setLoading(false);
    };

    fetchData();
  }, [pageType, currentPage, limit]);

  const getNameByLevel = (level, entityType = "user") => {
    if (entityType === "real_estate") {
      return "Entidade";
    }
    const usersLevelNames = {
      admin: "Admin",
      assistant: "Assistente",
      common: "Corretor",
    };
    return usersLevelNames[level] || String(level || "N/A").toUpperCase();
  };

  const getColorByLevel = (level, entityType = "user") => {
    if (entityType === "real_estate") {
      return "#7a41a0";
    }
    const usersLevelColor = {
      admin: "#000c2c",
      assistant: "#00408a",
      common: "#3d0066",
    };
    return usersLevelColor[level] || "#6c757d";
  };

  const getColorForCategory = (category) => {
    const categoryColors = {
      Construtora: "#6f42c1",
      Investidor: "#fd7e14",
      Imobiliária: "#20c997",
    };
    return categoryColors[category] || "#6c757d";
  };

  const dataFilter = entities.filter((entity) => {
    const searchTerm = search
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const nameToSearch = (entity.displayName || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const emailToSearch = (entity.email || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const documentToSearch = String(entity.displayDocument || "").replace(
      /\D/g,
      ""
    );
    const positionToSearch = getNameByLevel(
      entity.position,
      entity.entityType
    ).toLowerCase();
    const typeToSearch = (entity.type || "").toLowerCase();

    return (
      nameToSearch.includes(searchTerm) ||
      emailToSearch.includes(searchTerm) ||
      documentToSearch.includes(searchTerm) ||
      positionToSearch.includes(searchTerm) ||
      typeToSearch.includes(searchTerm)
    );
  });

  const addUser = async (newEntityDataFromModal) => {
    try {
      const apiPayload = {
        full_name: newEntityDataFromModal.full_name,
        document: newEntityDataFromModal.document,
        email: newEntityDataFromModal.email,
        password: newEntityDataFromModal.password,
        position: newEntityDataFromModal.position,
        real_estate_id: newEntityDataFromModal.real_estate || null,
        type: newEntityDataFromModal.type,
      };

      let response = null;

      if (newEntityDataFromModal.entityType === "real_estate") {
        response = await createRealEstate({
          name: apiPayload.full_name,
          type: apiPayload.type,
        });
      } else {
        response = await registerUser(apiPayload);
      }

      if (response) {
        fetchAndSetEntities();
        return true;
      } else {
        alert(`Erro ao criar: ${response?.message || "Verifique os dados."}`);
        return false;
      }
    } catch (error) {
      console.error("Erro ao registrar entidade:", error);
      alert("Erro ao criar entidade.");
      return false;
    }
  };

  const removeEntity = async (id, type) => {
    try {
      if (type === "user") {
        await deleteUser(id);
      } else if (type === "real_estate") {
        await deleteRealEstate(id);
      }
      setEntities((prev) =>
        prev.filter((e) => e.id !== id || e.entityType !== type)
      );
    } catch (error) {
      console.error(`Erro ao deletar ${type} com ID ${id}:`, error);
      alert(`Erro ao deletar ${type}.`);
    }
  };

  const editEntityHandler = async (updatedEntityDataFromModal) => {
    if (!entityToEdit) return false;
    try {
      let response;
      if (entityToEdit.entityType === "user") {
        const apiPayload = {
          full_name: updatedEntityDataFromModal.full_name,
          document: updatedEntityDataFromModal.document,
          email: updatedEntityDataFromModal.email,
          position: updatedEntityDataFromModal.position,
          real_estate_id: updatedEntityDataFromModal.real_estate || null,
          type: updatedEntityDataFromModal.type,
        };
        response = await updateUser(entityToEdit.id, apiPayload);
      } else if (entityToEdit.entityType === "real_estate") {
        response = await updateRealEstate(entityToEdit.id, {
          name: updatedEntityDataFromModal.full_name,
          cnpj: updatedEntityDataFromModal.document,
          type: updatedEntityDataFromModal.type,
        });

        if (response.message === "Imobiliária atualizada com sucesso") {
          alert("Imobiliária atualizada com sucesso!");
          fetchAndSetEntities();
          return true;
        } else {
          alert(
            `Erro ao atualizar imobiliária: ${
              response?.message || "Tente novamente."
            }`
          );
          return false;
        }
      }

      if (response && response.message === "Usuário atualizado com sucesso") {
        fetchAndSetEntities();
        alert(
          `${
            entityToEdit.entityType === "user" ? "Usuário" : "Imobiliária"
          } atualizado(a) com sucesso!`
        );
        return true;
      } else {
        alert(`Erro ao atualizar: ${response?.message || "Tente novamente."}`);
        return false;
      }
    } catch (error) {
      console.error("Erro ao atualizar entidade:", error);
      alert("Erro catastrófico ao atualizar.");
      return false;
    }
  };

  const handleOpenEditModalInternal = (entity) => {
    setEntityToEdit(entity);
    setOpenModal(true);
  };

  const handleOpenNewModal = () => {
    setEntityToEdit(null);
    setOpenModal(true);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <h1 className="title">
        {pageType === "user" ? "CONTROLE DE USUÁRIOS" : "CONTROLE DE ENTIDADES"}
      </h1>
      <section className="controll-users" style={{ marginBottom: "0px" }}>
        <div className="table-controllers">
          <div className="container-input-search">
            <i>
              <CiSearch />
            </i>
            <input
              type="text"
              placeholder="Busque por nome, e-mail, documento, nível, categoria..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button className="clear-button" onClick={() => setSearch("")}>
                x
              </button>
            )}
          </div>
          <button
            onClick={handleOpenNewModal}
            className="bt-add-new"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 15px",
            }}
          >
            {pageType === "user" ? "Novo Usuário" : "Nova Entidade"}
          </button>
        </div>
        <div className="table-wrapper">
          <table className="table-users">
            <thead>
              <tr>
                <th className="col-name">NOME / RESPONSÁVEL</th>
                {pageType === 'user' ? (
                  <th className="col-permissoes">TIPO / NÍVEL</th>
                ) : (
                  <th className="col-categoria">CATEGORIA</th>
                )}
                <th className="col-opcoes">OPÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {dataFilter.length > 0 ? (
                dataFilter
                  .sort((a, b) =>
                    (a.displayName || "").localeCompare(b.displayName || "")
                  )
                  .map((row) => {
                    return (
                      <tr key={`${row.entityType}-${row.id}`}>
                        <td data-label="NOME / RESPONSÁVEL">
                          {(row.displayName || "N/A").toUpperCase()}
                        </td>
                        {pageType === 'user' ? (
                          <td data-label="TIPO / NÍVEL" className="permissions">
                            <span
                              style={{
                                backgroundColor: getColorByLevel(
                                  row.position,
                                  row.entityType
                                ),
                              }}
                            >
                              {getNameByLevel(
                                row.position,
                                row.entityType
                              ).toUpperCase()}
                            </span>
                          </td>
                        ) : (
                          <td data-label="CATEGORIA" className="permissions">
                            <span
                              style={{
                                backgroundColor: getColorForCategory(row.type),
                              }}
                            >
                              {(row.type || "N/A").toUpperCase()}
                            </span>
                          </td>
                        )}
                        <td data-label="OPÇÕES">
                          <div className="action-buttons">
                            <button
                              className="edit-button"
                              onClick={() => handleOpenEditModalInternal(row)}
                              title="Editar"
                            >
                              <FaUserEdit />
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => {
                                setShowDeleteModal(true);
                                setEntityToDelete({
                                  id: row.id,
                                  type: row.entityType,
                                });
                              }}
                              title="Deletar"
                            >
                              <FaTrashAlt />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    style={{
                      textAlign: "center",
                      padding: "15px",
                      color: "#aaa",
                    }}
                  >
                    Nenhuma entidade encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* CONDICIONAL DE PAGINAÇÃO: SÓ EXIBE SE pageType === 'user' */}
      {pageType === "user" && (
        <div className="pagination-container" style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          gap: "20px", 
          padding: "15px 0",
          marginTop: "0px"
        }}>
          <div className="pagination-limit">
            <select 
              value={limit} 
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ padding: "5px 10px", borderRadius: "4px", cursor: "pointer" }}
            >
              <option value={25}>25 itens</option>
              <option value={50}>50 itens</option>
              <option value={100}>100 itens</option>
            </select>
          </div>

          <div className="pagination-controls" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <button 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              style={{ 
                background: "none", 
                border: "none", 
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.5 : 1,
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              }}
            >
              <FaChevronLeft />
            </button>

            <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Página {currentPage} de {totalPages}
            </span>

            <button 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              style={{ 
                background: "none", 
                border: "none", 
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.5 : 1,
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              }}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {openModal && (
        <UserManipulation
          pageType={pageType}
          newUser={!entityToEdit}
          user={entityToEdit}
          onAddUser={addUser}
          onEditUser={editEntityHandler}
          openModal={openModal}
          setOpenModal={setOpenModal}
          imobiliariaVinculos={realEstateOptions}
        />
      )}

      {showDeleteModal && entityToDelete && (
        <ModalConfirmAction
          isOpen={showDeleteModal}
          title={`Confirmação de Exclusão de ${
            entityToDelete.type === "user" ? "Usuário" : "Entidade"
          }`}
          onClose={() => {
            setShowDeleteModal(false);
            setEntityToDelete(null);
          }}
          descriptionAction={`Tem certeza que deseja excluir "${
            entities.find(
              (e) =>
                e.id === entityToDelete.id &&
                e.entityType === entityToDelete.type
            )?.displayName || entityToDelete.id
          }" permanentemente?`}
          buttonConfirmAction={() => {
            removeEntity(entityToDelete.id, entityToDelete.type);
            setShowDeleteModal(false);
            setEntityToDelete(null);
          }}
          textButtonConfirmAction="DELETAR"
          buttonCancelAction={() => {
            setShowDeleteModal(false);
            setEntityToDelete(null);
          }}
          textButtonCancelAction="CANCELAR"
        />
      )}
    </>
  );
}